import { List } from '@mui/material';
import { BasicListItem } from './ListItem';
import { memo } from 'react';

import { Selection, type Node, type NodeGroup } from './types';
import type { TreeState } from './useTreeState';
import { capitalize } from '@utils/index';
// import { Virtualize } from '@components/Virtualize';

const ListMemo = memo(List);

type TreeNodesProps = {
    // Necessary for virtualization
    root: HTMLDivElement | null;
    nodes: Node[];
    selection: TreeState;
    /**
     * Handler for when a {@link Node} is selected.
     */
    onNodeSelect: (node: Node) => void;
    /**
     * Handler for when a {@link NodeGroup} is selected.
     */
    onGroupSelect: (group: NodeGroup) => void;
    /**
     * The nesting level of the provided node set. Adds `paddingLeft` to each node.
     */
    level?: number;
    dense?: boolean;
};

// ! This component is recursive.
export const TreeNodes = ({
    root,
    nodes,
    selection,
    level = 0,
    dense = false,
    onNodeSelect,
    onGroupSelect,
}: TreeNodesProps): JSX.Element => {
    return (
        <>
            {nodes.map((node) => {
                const selected = selection[node.type] === node.id;

                return (
                    // todo: Virtualize?
                    // <Virtualize key={`list-item-${node.type}/${node.id}`} root={root!} height={56}>
                    // Render Node
                    <BasicListItem
                        key={`list-item-${node.type}/${node.id}`}
                        // ? Only auto-scroll to company nodes
                        focus={node.type === Selection.Company}
                        left={level}
                        nesting={Boolean(node.children.length)}
                        open={selected}
                        selected={selected}
                        primary={node.name}
                        secondary={capitalize(node.type)}
                        onClick={() => onNodeSelect(node)}>
                        {Boolean(node.children.length) && (
                            <ListMemo disablePadding dense={dense}>
                                {node.children.map((group) => {
                                    return (
                                        // Render Node Group
                                        <BasicListItem
                                            nesting
                                            open={selected && selection.groups.includes(group.type)}
                                            key={`list-item-${node.type}/${node.id}-${group.name}`}
                                            disabled={!group.nodes.length}
                                            left={level + 1}
                                            primary={group.name}
                                            secondary={`Under ${node.type}: ${node.name}`}
                                            onClick={() => onGroupSelect(group)}>
                                            <ListMemo disablePadding dense={dense}>
                                                {/* Recursively render the next set of Nodes */}
                                                <TreeNodes
                                                    nodes={group.nodes}
                                                    level={level + 2}
                                                    selection={selection}
                                                    onNodeSelect={onNodeSelect}
                                                    onGroupSelect={onGroupSelect}
                                                    root={root}
                                                />
                                            </ListMemo>
                                        </BasicListItem>
                                    );
                                })}
                            </ListMemo>
                        )}
                    </BasicListItem>
                    // </Virtualize>
                );
            })}
        </>
    );
};
